import {
  OnComponentClick,
  OnComponentDoubleClick,
  UrlFieldValue,
  OnReferenceClick,
  resolveOtherLabelPopover,
  resolveComponentLabelPopover,
} from '@ardoq/graph';
import { BulkHoverPopover } from '@ardoq/popovers';
import { ReferenceDirection } from '@ardoq/api-types';
import { DependencyMapViewNode as DependencyMapViewNodeBase } from './DependencyMapViewNode';
import { DependencyMapNodeViewModel, OnUrlIconClick } from './types';
import { DependencyMap } from './DependencyMap';
import { NodesContainer } from './NodesContainer';
import { ReactNode, useRef } from 'react';
import { usePreventWheelZoom } from '@ardoq/hooks';
import { resolveReferenceLabelPopover } from './view/DependencyMapReferenceLabel';

const resolveDependencyMapLabelPopover = (
  element: HTMLElement | SVGElement
): ReactNode | null =>
  resolveOtherLabelPopover(element) ??
  resolveReferenceLabelPopover(element) ??
  resolveComponentLabelPopover(element);

type DependencyMapVisualizationProps = {
  viewInstanceId: string;
  nodes: DependencyMapNodeViewModel[];
  colorByReference: boolean;
  selectedComponentId: string | null;
  hoveredComponentId: string | null;
  focusedComponentId: string | null;
  showVertical: boolean;
  setHoveredComponentId: (componentId: string | null) => void;
  setFocusedComponentId: (componentId: string | null) => void;
  onComponentClick: OnComponentClick;
  onReferenceClick?: OnReferenceClick;
  onComponentDoubleClick: OnComponentDoubleClick;
  onUrlIconClick: OnUrlIconClick;
  urlFieldValuesByComponentId: Map<string, UrlFieldValue[] | null>;
  urlFieldValuesByReferenceId: Map<string, UrlFieldValue[] | null>;
  contextMenuDataAttribute?: string;
  nodesContainerRef?: React.RefObject<HTMLDivElement | null>;
  onContainerClick?: VoidFunction;
  isNewConditionalFormatting: boolean;
  ariaLabel?: string;
  /**
   * App-specific popover content, composed into this view's single delegated
   * `BulkHoverPopover` so callers don't mount a second listener on the same
   * container. Returns null for elements it doesn't own.
   */
  resolveExtraContent?: (element: HTMLElement | SVGElement) => ReactNode | null;
};

export const DependencyMapVisualization = ({
  viewInstanceId,
  nodes,
  colorByReference,
  selectedComponentId,
  hoveredComponentId,
  focusedComponentId,
  showVertical,
  setHoveredComponentId,
  setFocusedComponentId,
  onComponentClick,
  onReferenceClick,
  onComponentDoubleClick,
  onUrlIconClick,
  urlFieldValuesByComponentId,
  urlFieldValuesByReferenceId,
  contextMenuDataAttribute,
  nodesContainerRef,
  onContainerClick,
  isNewConditionalFormatting,
  ariaLabel,
  resolveExtraContent,
}: DependencyMapVisualizationProps) => {
  const localNodesContainerRef = useRef<HTMLDivElement | null>(null);
  const resolvedNodesContainerRef = nodesContainerRef ?? localNodesContainerRef;

  usePreventWheelZoom(resolvedNodesContainerRef);

  return (
    <NodesContainer
      ref={resolvedNodesContainerRef}
      $showVertical={showVertical}
      $ariaLabel={ariaLabel}
      onClick={onContainerClick}
    >
      {nodes.map(node => (
        <DependencyMapViewNodeBase
          key={node.id}
          viewInstanceId={viewInstanceId}
          node={node}
          referenceDirection={ReferenceDirection.NONE}
          headerLabel={null}
          subLabel={node.subLabel}
          colorByReference={colorByReference}
          className={node.className}
          iconColor={node.iconColor}
          fill={node.fill}
          stroke={node.stroke}
          selectedComponentId={selectedComponentId}
          hoveredComponentId={hoveredComponentId}
          setHoveredComponentId={setHoveredComponentId}
          setFocusedComponentId={setFocusedComponentId}
          focusedComponentId={focusedComponentId}
          showVertical={showVertical}
          isRoot={true}
          isCollapsedReference={false}
          onComponentClick={onComponentClick}
          onReferenceClick={onReferenceClick}
          onComponentDoubleClick={onComponentDoubleClick}
          onUrlIconClick={onUrlIconClick}
          urlFieldValuesByComponentId={urlFieldValuesByComponentId}
          urlFieldValuesByReferenceId={urlFieldValuesByReferenceId}
          referenceIds={null}
          siblingCount={nodes.length - 1}
          contextMenuDataAttribute={contextMenuDataAttribute}
          isNewConditionalFormatting={isNewConditionalFormatting}
          DependencyMap={DependencyMap}
        />
      ))}
      <BulkHoverPopover
        rootRef={resolvedNodesContainerRef}
        resolveContent={element =>
          resolveDependencyMapLabelPopover(element) ??
          resolveExtraContent?.(element) ??
          null
        }
      />
    </NodesContainer>
  );
};
